import { create } from 'zustand';
import { clsx } from 'clsx';

/**
 * Notifications éphémères Phase 2 — store Zustand minimal.
 * Pas de dépendance externe : utile pour les confirmations de mutations
 * (valider/rejeter dossier, créer client, ajouter moyen de paiement).
 */

type ToastKind = 'success' | 'error' | 'info';

interface ToastItem {
  id: string;
  kind: ToastKind;
  message: string;
}

interface ToastState {
  toasts: ToastItem[];
  push: (kind: ToastKind, message: string) => void;
  dismiss: (id: string) => void;
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (kind, message) => {
    const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    set((s) => ({ toasts: [...s.toasts, { id, kind, message }] }));
    // Auto-dismiss après 5s
    if (typeof window !== 'undefined') {
      window.setTimeout(() => {
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
      }, 5000);
    }
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export function toast(kind: ToastKind, message: string): void {
  useToastStore.getState().push(kind, message);
}

const KIND_STYLES: Record<ToastKind, string> = {
  success: 'border-ok/40 bg-ok/10 text-ok',
  error: 'border-danger/40 bg-danger/10 text-danger',
  info: 'border-navy-200 bg-navy-50 text-navy-700',
};

export function ToastViewport(): JSX.Element {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);
  if (toasts.length === 0) {
    return <></>;
  }
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed right-4 top-4 z-[100] flex w-80 flex-col gap-2"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          role="status"
          className={clsx(
            'pointer-events-auto flex items-start gap-3 rounded-card border px-4 py-3 font-sans text-sm shadow-lift',
            KIND_STYLES[t.kind],
          )}
        >
          <span className="flex-1 leading-snug">{t.message}</span>
          <button
            type="button"
            aria-label="Fermer la notification"
            className="rounded p-1 opacity-70 hover:bg-black/5 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-current"
            onClick={() => dismiss(t.id)}
          >
            <svg viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor">
              <path d="M6 6l8 8M14 6l-8 8" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}
